import { gsap } from 'gsap'
import PropTypes from 'prop-types'
import {
  createContext,
  forwardRef,
  memo,
  useCallback,
  useContext,
  useMemo
} from 'react'
import constants from 'app/constants'
import useFallbackRef from 'lib/react/hooks/useFallbackRef'
import useHorizontalScroll from 'lib/react/hooks/useHorizontalScroll'
import useScrollPrompt from 'lib/react/hooks/useScrollPrompt'
import classNames from 'lib/util/classNames'
import ButtonScrollPrompt from '../../elements/ButtonScrollPrompt/ButtonScrollPrompt'
import styles from './ScrollRegion.module.scss'

const ScrollRegionContext = createContext({
  ref: null,
  scrollToTop: (duration = 0) => {
    gsap.to(window, {
      delay: 0.01,
      duration,
      scrollTo: { y: 0 },
      ease: 'power2.out'
    })
  }
})

export const useScrollRegionContext = () => {
  const context = useContext(ScrollRegionContext)

  return context
}

/**
 * The `ScrollRegion`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
const ScrollRegion = forwardRef((props, forwardedRef) => {
  const {
    Tag,
    children,
    className,
    disablePointerScroll,
    type,
    useScrollPrompt: doesUseScrollPrompt,
    useScrollbar,
    ...rest
  } = props
  const ref = useFallbackRef(forwardedRef)
  const breakpoint = useMemo(() => {
    if (type === 'hybrid') {
      // Is hybrid (scrolls vertical on large screens,
      // horizontal on small)
      return constants.BREAKPOINTS.md
    }

    if (type === 'horizontal') {
      // Horizontal scroll
      return Number.MAX_SAFE_INTEGER
    }

    return 0
  }, [type])
  const { overflowDirection, shouldShow: shouldShowScrollPrompt } =
    useScrollPrompt(ref)
  const classNameOutput = useMemo(
    () =>
      classNames([
        className,
        styles.scrollRegion,
        styles[`direction-${type}`],
        useScrollbar && styles.useScrollbar,
        doesUseScrollPrompt && shouldShowScrollPrompt && styles.useScrollPrompt
      ]),
    [className, type, useScrollbar, doesUseScrollPrompt, shouldShowScrollPrompt]
  )

  /**
   * Handle click scroll prompt
   */
  const handleClickScrollPrompt = useCallback(() => {
    const { clientHeight, clientWidth } = ref.current

    ref.current.scrollBy({
      [overflowDirection === 'vertical' ? 'top' : 'left']:
        overflowDirection === 'vertical' ? clientHeight : clientWidth,
      behavior: 'smooth'
    })
  }, [overflowDirection])

  /**
   * Handle scroll to top
   */
  const scrollToTop = useCallback((duration = 0) => {
    gsap.to(ref.current, {
      delay: 0.01,
      duration,
      scrollTo: { x: 0, y: 0 },
      ease: 'power2.out'
    })
  }, [])

  useHorizontalScroll(ref, breakpoint, disablePointerScroll)

  const ctx = useMemo(
    () => ({
      scrollToTop,
      ref
    }),
    [scrollToTop]
  )

  return (
    <ScrollRegionContext.Provider value={ctx}>
      <Tag ref={ref} className={classNameOutput} {...rest}>
        {children}
      </Tag>
      {doesUseScrollPrompt && (
        <ButtonScrollPrompt
          isActive={shouldShowScrollPrompt}
          direction={overflowDirection}
          onClick={handleClickScrollPrompt}
        />
      )}
    </ScrollRegionContext.Provider>
  )
})

// Display name
ScrollRegion.displayName = 'ScrollRegion'

/** @type {object} */
ScrollRegion.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  Tag: PropTypes.elementType,
  type: PropTypes.string,
  useScrollbar: PropTypes.bool,
  useScrollPrompt: PropTypes.bool,
  disablePointerScroll: PropTypes.bool
}

/** @type {object} */
ScrollRegion.defaultProps = {
  children: [],
  className: '',
  Tag: 'div',
  type: 'vertical',
  useScrollbar: true,
  useScrollPrompt: true,
  disablePointerScroll: false
}

// Memoize
export default memo(ScrollRegion)
